import React, { useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, Globe, Smartphone } from 'lucide-react';
import { cn } from '../../../lib/utils';

const projects = [
  {
    title: "Expo City Dubai Mobile App",
    hook: "Powering a Smart City Ecosystem",
    description: "A comprehensive mobile application that transforms visitor experiences through seamless navigation, real-time updates, and interactive features.",
    image: "/IMG_1043.PNG",
    type: "mobile",
    mockup: {
      screen1: "/IMG_1043.PNG",
      screen2: "/IMG_1045.PNG",
      screen3: "/IMG_1046.PNG"
    },
    link: "/projects/expo-city-app"
  },
  {
    title: "COP28 Digital Platform",
    hook: "Facilitating Global Climate Action",
    description: "An integrated digital platform enabling real-time collaboration, data sharing, and engagement for climate leaders worldwide.",
    image: "https://images.unsplash.com/photo-1473186578172-c141e6798cf4?auto=format&fit=crop&q=80",
    type: "web",
    link: "/projects/cop28-platform"
  },
  {
    title: "Smart Retail Analytics",
    hook: "Revolutionizing Shopping Experiences",
    description: "Advanced analytics platform providing real-time insights into customer behavior, inventory management, and store performance.",
    image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&q=80",
    type: "web",
    link: "/projects/retail-analytics"
  },
  {
    title: "Connected Transport Hub",
    hook: "Streamlining Urban Mobility",
    description: "Intelligent transportation system integrating multiple modes of transport for seamless urban mobility and reduced congestion.",
    image: "https://images.unsplash.com/photo-1494522358652-f30e61a60313?auto=format&fit=crop&q=80",
    type: "web",
    link: "/projects/transport-hub"
  }
];

const MobileProjectCard = ({ project, index }: { project: typeof projects[0], index: number }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    const img = new Image();
    img.src = project.image;
    img.onload = () => setImageLoaded(true);
  }, [project.image]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0 }}
      animate={isInView ? { opacity: 1 } : { opacity: 0 }}
      transition={{ duration: 0.6 }}
      className="group"
    >
      <div className="grid lg:grid-cols-[45%_55%] gap-8 items-center">
        {/* Mobile Mockup */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={imageLoaded && isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="relative"
        >
          <div className="relative aspect-[320/680] mx-auto max-w-[280px]">
            <motion.div
              className="absolute inset-0 rounded-[3rem] border-8 border-gray-800 bg-black overflow-hidden"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.4 }}
            >
              <motion.img
                src={project.image}
                alt={project.title}
                className="absolute inset-0 w-full h-full object-cover"
                initial={{ scale: 1.1 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.6 }}
                onLoad={() => setImageLoaded(true)}
              />
            </motion.div>
            {/* Phone Details */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-6 bg-black rounded-b-2xl" />
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-32 h-1 bg-gray-800 rounded-full" />
          </div>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 20 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="relative"
        >
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
            transition={{ duration: 0.4, delay: 0.4 }}
            className="flex items-center gap-2 text-accent mb-4"
          >
            <Smartphone className="w-5 h-5" />
            <span>Mobile App</span>
          </motion.div>
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
            transition={{ duration: 0.4, delay: 0.5 }}
            className="inline-block text-accent mb-4"
          >
            {project.hook}
          </motion.span>
          <motion.h3
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
            transition={{ duration: 0.4, delay: 0.6 }}
            className="text-3xl font-display font-semibold text-white mb-4"
          >
            {project.title}
          </motion.h3>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
            transition={{ duration: 0.4, delay: 0.7 }}
            className="text-gray-400 mb-8"
          >
            {project.description}
          </motion.p>
          <motion.a
            href={project.link}
            initial={{ opacity: 0, x: -10 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -10 }}
            transition={{ duration: 0.4, delay: 0.8 }}
            whileHover={{ x: 10 }}
            className="inline-flex items-center gap-2 text-white font-medium hover:text-accent transition-colors duration-300"
          >
            <span>Read More</span>
            <ArrowRight className="w-5 h-5" />
          </motion.a>
        </motion.div>
      </div>
    </motion.div>
  );
};

const WebProjectCard = ({ project, index }: { project: typeof projects[0], index: number }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const isEven = index % 2 === 0;

  useEffect(() => {
    const img = new Image();
    img.src = project.image;
    img.onload = () => setImageLoaded(true);
  }, [project.image]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0 }}
      animate={isInView ? { opacity: 1 } : { opacity: 0 }}
      transition={{ duration: 0.6 }}
      className="group"
    >
      <div className={cn(
        "grid md:grid-cols-2 gap-8 items-center",
        isEven ? "" : "md:[grid-template-areas:'content_image']"
      )}>
        {/* Browser Mockup */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={imageLoaded && isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className={cn(
            "relative",
            !isEven && "md:[grid-area:image]"
          )}
        >
          <motion.div
            className="relative rounded-xl overflow-hidden bg-gray-900 shadow-2xl"
            whileHover={{ y: -5 }}
            transition={{ duration: 0.4 }}
          >
            {/* Browser Bar */}
            <div className="flex items-center gap-2 px-4 py-3 bg-gray-800">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <div className="w-3 h-3 rounded-full bg-green-500" />
              </div>
            </div>
            {/* Content */}
            <div className="relative aspect-[16/10] overflow-hidden">
              <motion.div
                className="absolute inset-0 bg-gradient-to-tr from-black/60 to-black/20 group-hover:from-black/40 group-hover:to-black/10 transition-colors duration-300 z-10"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              />
              <motion.img
                src={project.image}
                alt={project.title}
                className="w-full h-full object-cover"
                initial={{ scale: 1.1 }}
                animate={{ scale: 1 }}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.6 }}
                onLoad={() => setImageLoaded(true)}
              />
            </div>
          </motion.div>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, x: isEven ? 20 : -20 }}
          animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: isEven ? 20 : -20 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className={cn(
            "relative z-20",
            !isEven && "md:[grid-area:content]"
          )}
        >
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
            transition={{ duration: 0.4, delay: 0.4 }}
            className="flex items-center gap-2 text-accent mb-4"
          >
            <Globe className="w-5 h-5" />
            <span>Web Platform</span>
          </motion.div>
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
            transition={{ duration: 0.4, delay: 0.5 }}
            className="inline-block text-accent mb-4"
          >
            {project.hook}
          </motion.span>
          <motion.h3
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
            transition={{ duration: 0.4, delay: 0.6 }}
            className="text-3xl font-display font-semibold text-white mb-4"
          >
            {project.title}
          </motion.h3>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
            transition={{ duration: 0.4, delay: 0.7 }}
            className="text-gray-400 mb-8"
          >
            {project.description}
          </motion.p>
          <motion.a
            href={project.link}
            initial={{ opacity: 0, x: -10 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -10 }}
            transition={{ duration: 0.4, delay: 0.8 }}
            whileHover={{ x: 10 }}
            className="inline-flex items-center gap-2 text-white font-medium hover:text-accent transition-colors duration-300"
          >
            <span>Read More</span>
            <ArrowRight className="w-5 h-5" />
          </motion.a>
        </motion.div>
      </div>
    </motion.div>
  );
};

const ProjectShowcase = () => {
  return (
    <section id="projects" className="py-24 bg-black">
      <div className="container mx-auto px-4">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-4xl md:text-5xl font-display font-semibold text-white mb-16"
        >
          Projects That Define Our Expertise
        </motion.h2>

        <div className="space-y-24">
          {projects.map((project, index) => (
            project.type === 'mobile' ? (
              <MobileProjectCard
                key={project.title}
                project={project}
                index={index}
              />
            ) : (
              <WebProjectCard
                key={project.title}
                project={project}
                index={index}
              />
            )
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProjectShowcase;